import React, { useState, useEffect } from 'react';
import { collection, getDocs, addDoc, doc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase';
import './SuperAdminSchoolsView.css';
import './StudentDetailPages.css';
import StatCards from './StatCards';
import { schoolMatches } from '../utils/schoolScope';

function SuperAdminSchoolsView() {
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedSchool, setSelectedSchool] = useState(null);
  
  const [newSchool, setNewSchool] = useState({
    name: '',
    type: 'Primary',
    district: '',
    schoolCode: ''
  });

  useEffect(() => {
    loadSchools();
  }, []);

  const loadSchools = async () => {
    try {
      const snapshot = await getDocs(collection(db, 'schools'));
      const schoolsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setSchools(schoolsData);
    } catch (error) {
      console.error('Error loading schools:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddSchool = async (e) => {
    e.preventDefault();
    try {
      await addDoc(collection(db, 'schools'), {
        ...newSchool,
        isActive: true,
        createdAt: new Date().toISOString()
      });
      setNewSchool({ name: '', type: 'Primary', district: '', schoolCode: '' });
      setShowAddForm(false);
      loadSchools();
    } catch (error) {
      console.error('Error adding school:', error);
    }
  };

  const handleDeleteSchool = async (schoolId) => {
    if (window.confirm('Are you sure you want to delete this school?')) {
      try {
        await deleteDoc(doc(db, 'schools', schoolId));
        loadSchools();
      } catch (error) {
        console.error('Error deleting school:', error);
      }
    }
  };

  const handleViewDetails = (school) => {
    setSelectedSchool(school);
  };

  const loadSchoolStats = async (schoolId) => {
    try {
      const [users, students, classes, events] = await Promise.all([
        getDocs(collection(db, 'users')),
        getDocs(collection(db, 'students')),
        getDocs(collection(db, 'classes')),
        getDocs(collection(db, 'events'))
      ]);

      // Case-insensitive schoolId matching
      const schoolUsers = users.docs.filter(doc => schoolMatches(doc.data().schoolId, schoolId));
      const schoolStudents = students.docs.filter(doc => schoolMatches(doc.data().schoolId, schoolId));
      const schoolClasses = classes.docs.filter(doc => schoolMatches(doc.data().schoolId, schoolId));
      const schoolEvents = events.docs.filter(doc => schoolMatches(doc.data().schoolId, schoolId));

      const teachers = schoolUsers.filter(doc => doc.data().role === 'Teacher').length;
      const parents = schoolUsers.filter(doc => doc.data().role === 'Parent').length;
      const admins = schoolUsers.filter(doc => doc.data().role === 'Admin').length;

      return {
        totalUsers: schoolUsers.length,
        totalStudents: schoolStudents.length,
        totalTeachers: teachers,
        totalParents: parents,
        totalAdmins: admins,
        totalClasses: schoolClasses.length,
        totalEvents: schoolEvents.length
      };
    } catch (error) {
      console.error('Error loading school stats:', error);
      return null;
    }
  };

  if (loading) {
    return <div className="loading-container">Loading schools...</div>;
  }

  if (selectedSchool) {
    console.log('🔥 SuperAdminSchools: Opened school detail', selectedSchool.id);
    return (
      <SchoolDetailPage
        school={selectedSchool}
        onBack={() => setSelectedSchool(null)}
        loadStats={loadSchoolStats}
      />
    );
  }

  return (
    <div className="super-admin-schools-view">
      <div className="view-header">
        <h2>Schools Management</h2>
        <button 
          className="add-button"
          onClick={() => setShowAddForm(true)}
        >
          + Add School
        </button>
      </div>
      <StatCards items={[['Schools',schools.length],['Primary',schools.filter(s => s.type === 'Primary').length],['Secondary',schools.filter(s => s.type === 'Secondary').length],['College',schools.filter(s => s.type === 'College').length],['Active',schools.filter(s => s.isActive !== false).length],['Inactive',schools.filter(s => s.isActive === false).length]]} charts={[{title:'Schools by Type',type:'pie',data:['Primary','Secondary','College'].map(t => ({name:t,value:schools.filter(s => s.type === t).length}))}]} />

      {showAddForm && (
        <div className="add-form-card">
          <h3>Add New School</h3>
          <form onSubmit={handleAddSchool}>
            <div className="form-group">
              <label>School Name</label>
              <input
                type="text"
                value={newSchool.name}
                onChange={(e) => setNewSchool({ ...newSchool, name: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label>School Type</label>
              <select
                value={newSchool.type}
                onChange={(e) => setNewSchool({ ...newSchool, type: e.target.value })}
              >
                <option value="Primary">Primary</option>
                <option value="Secondary">Secondary</option>
                <option value="College">College</option>
              </select>
            </div>
            <div className="form-group">
              <label>District</label>
              <input
                type="text"
                value={newSchool.district}
                onChange={(e) => setNewSchool({ ...newSchool, district: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>School Code</label>
              <input
                type="text"
                value={newSchool.schoolCode}
                onChange={(e) => setNewSchool({ ...newSchool, schoolCode: e.target.value })}
              />
            </div>
            <div className="form-actions">
              <button type="button" onClick={() => setShowAddForm(false)}>Cancel</button>
              <button type="submit">Add School</button>
            </div>
          </form>
        </div>
      )}

      <div className="schools-grid">
        {schools.map(school => (
          <div key={school.id} className="school-card">
            <div className="school-icon">
              {school.type === 'Primary' ? '🏫' : school.type === 'Secondary' ? '🎓' : '📚'}
            </div>
            <div className="school-info">
              <h3>{school.name}</h3>
              <div className="school-meta">
                <span className="badge badge-type">{school.type}</span>
                {school.schoolCode && <span className="badge badge-code">{school.schoolCode}</span>}
              </div>
              <p className="district">{school.district}</p>
            </div>
            <div className="school-actions">
              <button 
                className="view-button"
                onClick={() => handleViewDetails(school)}
              >
                View Details
              </button>
              <button 
                className="delete-button"
                onClick={() => handleDeleteSchool(school.id)}
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>

    </div>
  );
}

function SchoolDetailPage({ school, onBack, loadStats }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      const schoolStats = await loadStats(school.id);
      setStats(schoolStats);
      setLoading(false);
    };
    fetchStats();
  }, [school.id, loadStats]);

  return (
    <div className="detail-page">
      <button className="detail-back-btn" onClick={onBack}>◄ Back</button>
      <div className="detail-hero">
        <div className="detail-hero-icon">
          {school.type === 'Primary' ? '🏫' : school.type === 'Secondary' ? '🎓' : '📚'}
        </div>
        <div>
          <h2 className="detail-page-title">{school.name}</h2>
          <p className="detail-page-subtitle">{school.district || 'No district'}</p>
        </div>
      </div>

      <div className="detail-card">
        <h4>School Details</h4>
        <div className="detail-rows">
          <div className="detail-row"><span className="detail-label">School Code</span><span className="detail-value">{school.schoolCode || 'N/A'}</span></div>
          <div className="detail-row"><span className="detail-label">Type</span><span className="detail-value">{school.type || 'N/A'}</span></div>
          <div className="detail-row"><span className="detail-label">District</span><span className="detail-value">{school.district || 'N/A'}</span></div>
          <div className="detail-row"><span className="detail-label">Status</span><span className="detail-value">{school.isActive === false ? 'Inactive' : 'Active'}</span></div>
        </div>
      </div>

      <div className="detail-card">
        <h4>School Statistics</h4>
        {loading ? (
          <div className="loading-container">Loading school data...</div>
        ) : stats ? (
          <div className="school-stats">
            <div className="stats-grid">
              <StatItem label="Total Users" value={stats.totalUsers} icon="👥" color="blue" />
              <StatItem label="Students" value={stats.totalStudents} icon="🎓" color="green" />
              <StatItem label="Teachers" value={stats.totalTeachers} icon="👨‍🏫" color="orange" />
              <StatItem label="Parents" value={stats.totalParents} icon="👨‍👩‍👧" color="purple" />
              <StatItem label="Admins" value={stats.totalAdmins} icon="👔" color="blue" />
              <StatItem label="Classes" value={stats.totalClasses} icon="📚" color="red" />
              <StatItem label="Events" value={stats.totalEvents} icon="📅" color="pink" />
            </div>
          </div>
        ) : (
          <div className="error-message">Failed to load school statistics</div>
        )}
      </div>
    </div>
  );
}

function StatItem({ label, value, icon, color }) {
  return (
    <div className={`stat-item stat-item-${color}`}>
      <div className="stat-icon">{icon}</div>
      <div className="stat-info">
        <div className="stat-value">{value}</div>
        <div className="stat-label">{label}</div>
      </div>
    </div>
  );
}

export default SuperAdminSchoolsView;
